import { useState } from "react";

import { Column, LabelWrapper, PercentInput, StatColumn, StatProps, Text, ToggleButtons } from "@bridgesplit/ui";
import { AbfLoanExpanded } from "@bridgesplit/abf-react";
import { formatOrDash, formatPercent, percentDecimalsToUi, percentUiToDecimals } from "@bridgesplit/utils";

import { AppButton } from "../../common";
import { useRefinanceProtection } from "./useRefinanceProtection";

enum ProtectionSetting {
    Off,
    On
}

const MIN_LQT_PERCENT = 1;
const MAX_LQT_PERCENT = 100;

type Form = { setting: ProtectionSetting; thresholdPercent: number | undefined };

const isLqtPercentInRange = (n: number | undefined): boolean =>
    n === undefined || (n >= MIN_LQT_PERCENT && n <= MAX_LQT_PERCENT);

const formatFloorPercent = (percent: number | undefined): string =>
    percent !== undefined ? formatPercent(percentUiToDecimals(percent)) : "Not set";

// Collapse to a single value when unchanged; otherwise render as a `before → after` pair.
const toStatChangeValue = (current: string, next: string): StatProps["value"] =>
    current === next ? current : [current, next];

function getRefinanceProtectionStats(
    currentLtv: number | undefined,
    persistedFloor: number | undefined,
    pendingFloor: number | undefined
): StatProps[] {
    return [
        {
            caption: "Current liquidation LTV",
            value: formatOrDash(currentLtv, () => formatPercent(currentLtv))
        },
        {
            caption: "Min refinance liquidation LTV",
            value: toStatChangeValue(formatFloorPercent(persistedFloor), formatFloorPercent(pendingFloor))
        }
    ];
}

export default function RefinanceProtectionSettings({ loanExpanded }: { loanExpanded: AbfLoanExpanded | undefined }) {
    const { currentLtv, floorPercent, submit, isLoading } = useRefinanceProtection(loanExpanded);

    const currentLtvPercent = currentLtv !== undefined ? percentDecimalsToUi(currentLtv) : undefined;

    const initialForm: Form = {
        setting: floorPercent !== undefined ? ProtectionSetting.On : ProtectionSetting.Off,
        thresholdPercent: floorPercent ?? currentLtvPercent
    };

    // Derived state: edits overlay the loaded constraint, so a refetch never clobbers user input.
    const [edits, setEdits] = useState<Form | undefined>(undefined);
    const form = edits ?? initialForm;

    const isOn = form.setting === ProtectionSetting.On;
    const thresholdValid = !isOn || (form.thresholdPercent !== undefined && isLqtPercentInRange(form.thresholdPercent));
    const changesMade =
        form.setting !== initialForm.setting || (isOn && form.thresholdPercent !== initialForm.thresholdPercent);

    const pendingFloor = isOn ? form.thresholdPercent : undefined;

    return (
        <Column spacing={2}>
            <Text variant="body2" color="caption">
                Set the lowest liquidation LTV this loan may be refinanced into.
            </Text>
            {/* Gate interactive controls until load resolves so an early edit can't drop the prefill. */}
            {!isLoading && (
                <ToggleButtons
                    value={form.setting}
                    setValue={(setting) => setEdits({ thresholdPercent: form.thresholdPercent, setting })}
                    options={[
                        { value: ProtectionSetting.On, label: "Set protection" },
                        { value: ProtectionSetting.Off, label: "No protection" }
                    ]}
                />
            )}
            {!isLoading && isOn && (
                <LabelWrapper
                    label="Minimum liquidation LTV"
                    tooltip="The tightest liquidation LTV a refinance may use"
                >
                    <PercentInput
                        placeholder="e.g. 80"
                        value={form.thresholdPercent}
                        setValue={(thresholdPercent) => setEdits({ setting: ProtectionSetting.On, thresholdPercent })}
                        validateNumber={isLqtPercentInRange}
                    />
                </LabelWrapper>
            )}
            <StatColumn
                loading={isLoading}
                stats={getRefinanceProtectionStats(currentLtv, floorPercent, pendingFloor)}
            />
            {!isLoading && (
                <AppButton
                    isTransaction={false}
                    asyncCta={{
                        onClickWithResult: () => submit(isOn ? form.thresholdPercent : undefined),
                        closeDialog: "onSuccess"
                    }}
                    disabled={!changesMade || !thresholdValid}
                >
                    {isOn ? "Save protection" : "Remove protection"}
                </AppButton>
            )}
        </Column>
    );
}
